import React from "react";
import type { PriceConfigType, PriceType } from "../../../../../../../common-deprecated/types/CommonTypes";
import { hideDiscountInfo } from "../../../../../../../common-deprecated/priceUtils";
import PricePromoIcon from "./PricePromoIcon";
import * as Styles from "../../../../../../../common-deprecated/styles/v2/toyota/custom/price/CashPrice";
import { BnbLabel } from "../../../../utils/labels";
import {
    formatAEMCashPrice,
    hasDualCurrency,
} from "../../../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { useBnbSelector } from "../../../../redux/store";

export type CashPriceType = {
    price: PriceType;
    priceConfig: PriceConfigType;
    cultureName: string;
    isLarge?: boolean;
    // used for showing cash price underneath monthly
    small?: boolean;
    // shows loader when true
    showLoader?: boolean;
};
const CashPrice = (props: CashPriceType): JSX.Element | null => {
    const { price, priceConfig, cultureName, small, isLarge, showLoader } = props;
    const { highlight, discount, cash, promotions, licenseFee } = price;

    const hasSecondaryPrice = useBnbSelector((state) => hasDualCurrency(state.commonSettings));

    if (!price.cash || !priceConfig.priceEnabled) return null;

    const hideDiscount = hideDiscountInfo(priceConfig);
    if (showLoader)
        return (
            <Styles.Text isLarge={!!isLarge} isSmall={small}>
                <BnbLabel label="priceLoading" fallbackLabel="spinLoading" />
            </Styles.Text>
        );

    // Highlight is also a type of promotion which does not alter the price but should show the text as highlighted.
    if ((highlight || discount !== cash || promotions.length > 0) && !hideDiscount) {
        const cashFormatted = formatAEMCashPrice(cash, cultureName, priceConfig);
        const discountFormatted = formatAEMCashPrice(discount, cultureName, priceConfig, licenseFee);
        return (
            <>
                <Styles.PricesWrapper>
                    <div>
                        <Styles.Text
                            dangerouslySetInnerHTML={{ __html: discountFormatted }}
                            isLarge={!small && (discount !== cash || promotions.length > 0)}
                            isSmall={small}
                            mt={2}
                        />

                        {promotions.length > 0 && !hasSecondaryPrice && <PricePromoIcon promotions={promotions} />}
                    </div>

                    {!highlight && discount !== cash && (
                        <Styles.TextLineThrough
                            dangerouslySetInnerHTML={{ __html: cashFormatted }}
                            fontSize="13px"
                            isSmall={!!small}
                        />
                    )}
                </Styles.PricesWrapper>
                {hasSecondaryPrice && (
                    <Styles.PricesWrapper>
                        <div>
                            <Styles.Text
                                dangerouslySetInnerHTML={{
                                    __html: formatAEMCashPrice(discount, cultureName, priceConfig, licenseFee, true),
                                }}
                                isLarge={!small && (discount !== cash || promotions.length > 0)}
                                isSmall={small}
                                mt={2}
                            />

                            {promotions.length > 0 && <PricePromoIcon promotions={promotions} />}
                        </div>

                        {!highlight && discount !== cash && (
                            <Styles.TextLineThrough
                                dangerouslySetInnerHTML={{
                                    __html: formatAEMCashPrice(cash, cultureName, priceConfig, licenseFee, true),
                                }}
                                fontSize="13px"
                                isSmall={!!small}
                            />
                        )}
                    </Styles.PricesWrapper>
                )}
            </>
        );
    }

    return (
        <Styles.PricesWrapper>
            <Styles.Text
                isLarge={!!isLarge}
                isSmall={small}
                dangerouslySetInnerHTML={{
                    __html: formatAEMCashPrice(
                        hideDiscountInfo(priceConfig) ? discount : cash,
                        cultureName,
                        priceConfig,
                        licenseFee,
                        false,
                    ),
                }}
            />
            {/* Dual currency price */}
            {hasSecondaryPrice && (
                <Styles.Text
                    isLarge={!!isLarge}
                    isSmall={small}
                    dangerouslySetInnerHTML={{
                        __html: formatAEMCashPrice(
                            hideDiscountInfo(priceConfig) ? discount : cash,
                            cultureName,
                            priceConfig,
                            licenseFee,
                            true,
                        ),
                    }}
                />
            )}
        </Styles.PricesWrapper>
    );
};

export default CashPrice;
